/**
 * Native shapes for the v2 inbox — matches what the API routes return
 * (`/api/workspaces/{ws}/inbox-v2/conversations` and the timeline route).
 *
 * No legacy DbConversationWithRelations shim — consumers read the snake_case
 * payload as the server returns it.
 */

/**
 * Sentinel carried inside the `assignees: string[]` / `selectedAssignees:
 * Set<string>` filter value to mean "unassigned". Distinguishable from real
 * assignees because those are UUIDs and this value is not UUID-shaped.
 * Selecting it ORs a `c.assigned_user_id IS NULL` predicate with the selected
 * people. Lives here (a client-safe, prisma-free module) so client filter
 * components can import it without pulling `@prisma/client` into the bundle.
 */
export const UNASSIGNED_ASSIGNEE = 'unassigned'

export type ConversationStatusV2 = 'TODO' | 'FOLLOW_UP' | 'DONE' | 'ARCHIVED'
export type ConversationPriorityV2 = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
export type MessageDirectionV2 = 'INBOUND' | 'OUTBOUND' | null

interface InboxV2ContactType {
  alias_of: string | null
  color: string | null
  icon: string | null
  id: string
  name: string | null
  slug: string | null
}

export interface InboxV2Contact {
  contact_type?: InboxV2ContactType | null
  contact_type_id?: string | null
  display_name: string | null
  first_name: string | null
  id: string
  is_group: boolean
  last_name: string | null
  methods?: Array<{
    id: string
    type: string
    value: string
    raw_value: string | null
    is_primary: boolean
    is_active: boolean
    has_whatsapp: boolean | null
  }>
  /**
   * Lowercase domain part of the contact's primary email (after the @).
   * Drives the EMAIL channel badge: when the domain is in
   * `EMAIL_DOMAIN_ICONS`, the badge swaps the generic envelope for the
   * sender's brand mark. Null when the contact has no email method.
   */
  primary_email_domain?: string | null
}

export interface InboxV2IntegrationReservation {
  amount_paid?: string | number | null
  booked_at?: string | null
  channel?: string | null
  checkin_date: string
  checkin_time?: string | null
  checkout_date: string
  checkout_time?: string | null
  cleaning_fee?: string | number | null
  confirmation_code?: string | null
  currency?: string | null
  custom_fields?: Record<string, unknown> | null
  email?: string | null
  external_id?: string | null
  first_name?: string | null
  guest_count?: number | null
  guest_name?: string | null
  guest_portal_url?: string | null
  id: string | null
  inbox_pms_display_status?: string | null
  inbox_pms_raw_status?: string | null
  inbox_pms_status?: string | null
  is_preapproved?: boolean | null
  last_name?: string | null
  notes?: string | null
  ota_reference?: string | null
  payment_status?: string | null
  phone?: string | null
  preapproved_at?: string | null
  preapproved_by?: string | null
  property_id?: string | null
  /** 'raw' when sourced from gy_/ha_/cy_ tables for an unsynced inquiry. */
  source?: 'raw'
  special_requests?: string | null
  status: string
  total_amount?: string | number | null
}

export interface InboxV2IntegrationProperty {
  address?: string | null
  /** Cover photo URL (thumbnail preferred, external fallback). */
  cover_url: string | null
  custom_fields?: Record<string, unknown> | null
  displayName?: string | null
  guest_display_name?: string | null
  id: string
  internal_code?: string | null
  internal_name?: string | null
  name: string
  workspace_id?: string | null
}

export interface InboxV2Integration {
  deleted_at?: string | null
  email_address_id?: string | null
  group_name: string | null
  /** SMS/Quo group participant roster from `conversation_integrations_v2`
   *  metadata (PROD-8047). Absent for WhatsApp and non-group integrations. */
  group_participants?: Array<{
    name?: string | null
    phone?: string | null
    contact_name?: string | null
    is_workspace_line?: boolean
  }> | null
  id: string
  integration_token_id?: string | null
  is_active?: boolean
  is_group: boolean
  is_inquiry?: boolean
  ota: string | null
  /** Workspace endpoint IDs — used by the "From" filter to scope the
   *  conversation list to a specific phone / email / connected account. */
  phone_number_id?: string | null
  property?: InboxV2IntegrationProperty | null
  provider: string
  reservation?: InboxV2IntegrationReservation | null
  reservation_id?: string | null
  value: string
}

export interface InboxV2LastEvent {
  direction: MessageDirectionV2
  preview: string | null
  type: string
}

export interface InboxV2Tag {
  color: string
  icon: string | null
  id: string
  name: string
}

/** A timeline event whose preview matched the active search query.
 *  Returned by the conversations list endpoint when `?q=` is present so
 *  the inbox row can expand into a sub-list of matching messages. */
export interface InboxV2MatchingEvent {
  direction: 'INBOUND' | 'OUTBOUND' | null
  id: string
  occurred_at: string
  preview: string | null
  source_id: string | null
  type: string
}

export interface InboxV2ConversationRow {
  /** auth.users.id (uuid). Null when nobody is assigned. */
  assigned_user_id: string | null
  /** Number of internal comments on this conversation. Drives the list-row
   *  note indicator so a comment left after the thread is closed is visible
   *  without opening it (PROD-7800). */
  comment_count: number
  contact: InboxV2Contact | null
  /** Persisted operator reply draft, when present. */
  draft_content?: string | null
  draft_updated_at?: string | null
  /** True while the conversation has at least one open, actionable AI escalation.
   *  Uses the same predicate as the escalation filter, the Escalations view and
   *  the sidebar count, so it clears as soon as the escalation is handled or
   *  dismissed (PROD-8442). Drives the list-row escalation indicator. */
  has_open_escalation: boolean
  has_unread: boolean
  id: string
  integrations: InboxV2Integration[]
  last_event: InboxV2LastEvent | null
  last_occurred_at: string | null
  latest_inbound_event_id?: string | null
  /** Empty unless the active query is a `?q=` search. Up to 3 message-style
   *  timeline events whose preview ILIKE-matched the query, newest first. */
  matching_events: InboxV2MatchingEvent[]
  priority: ConversationPriorityV2
  property_id: string | null
  reservation_tags: InboxV2Tag[]
  /** Present only when the conversation has exactly one active CI and it is an email thread. */
  single_email_thread_subject: string | null
  snoozed_until: string | null
  status: ConversationStatusV2
  stay_id: string | null
  tags: InboxV2Tag[]
  unread_count: number
  /** Row version for realtime patch ordering — server `conversations_v2.updated_at`.
   *  Patches with an older `updated_at` than the cached row are skipped. */
  updated_at: string
}

export interface InboxV2ListResponse {
  data: InboxV2ConversationRow[]
  has_more: boolean
  limit: number
  offset: number
}

/**
 * Server-side filter shape accepted by `/api/workspaces/{ws}/inbox-v2/conversations`.
 * Shared between the list page hook (`useInboxV2List`) and the chat-list sidebar
 * (`getConversations`) so both surfaces send identical query params and the
 * server is the single source of truth for filtering.
 */
export interface InboxV2FilterQuery {
  readState?: 'read' | 'unread'
  bookedFrom?: string | null
  bookedTo?: string | null
  responseStatuses?: string[]
  aiReplied?: boolean
  assigned?: 'me' | 'others' | 'unassigned' | null
  assignees?: string[]
  channels?: string[]
  contactIds?: string[]
  departments?: string[]
  endpoints?: string[]
  escalations?: boolean
  from?: string | null
  groups?: string[]
  inboxes?: string[]
  integrations?: string[]
  listings?: string[]
  lastResponse?: 'guest' | 'app'
  q?: string
  reservationCode?: string
  reservationTags?: string[]
  /** A chat result the list is narrowed to, as the Expand link encodes it. */
  scope?: string
  statuses?: string[]
  stayDates?: string[]
  tags?: string[]
  to?: string | null
}

const ARRAY_FILTER_PARAMS = [
  ['listings', 'listings'],
  ['groups', 'groups'],
  ['channels', 'channels'],
  ['statuses', 'statuses'],
  ['stayDates', 'stay_dates'],
  ['endpoints', 'endpoints'],
  ['integrations', 'integrations'],
  ['tags', 'tags'],
  ['reservationTags', 'reservation_tags'],
  ['assignees', 'assignees'],
  ['departments', 'departments'],
  ['contactIds', 'contacts'],
] as const

function applyArrayFilters(params: URLSearchParams, filters: InboxV2FilterQuery): void {
  for (const [field, parameter] of ARRAY_FILTER_PARAMS) {
    const values = filters[field]
    if (values?.length) {
      params.set(parameter, values.join(','))
    }
  }
}

/** Serialise an `InboxV2FilterQuery` onto an existing URLSearchParams. */
export function applyInboxV2FiltersToParams(params: URLSearchParams, f?: InboxV2FilterQuery): void {
  if (!f) {
    return
  }
  if (f.readState) {
    params.set('read_state', f.readState)
  }
  if (f.q) {
    params.set('q', f.q)
  }
  if (f.from) {
    params.set('from', f.from)
  }
  if (f.to) {
    params.set('to', f.to)
  }
  applyArrayFilters(params, f)
  if (f.bookedFrom) params.set('booked_from', f.bookedFrom)
  if (f.bookedTo) params.set('booked_to', f.bookedTo)
  if (f.inboxes?.length === 1) {
    params.set('inbox', f.inboxes[0] ?? '')
  } else if (f.inboxes && f.inboxes.length > 1) {
    params.set('inboxes', f.inboxes.join(','))
  }
  if (f.escalations) {
    params.set('escalations', '1')
  }
  if (f.responseStatuses?.length) {
    params.set('response_status', f.responseStatuses.join(','))
  }
  if (f.aiReplied) {
    params.set('ai_replied', '1')
  }
  if (f.lastResponse) {
    params.set('last_response', f.lastResponse)
  }
  if (f.assigned) {
    params.set('assigned', f.assigned)
  }
  if (f.reservationCode) {
    params.set('reservation_code', f.reservationCode)
  }
  if (f.scope) {
    params.set('scope', f.scope)
  }
}

/** Stable signature for an `InboxV2FilterQuery` (queryKey / dep arrays). */
export function inboxV2FiltersSignature(f?: InboxV2FilterQuery): string {
  if (!f) {
    return ''
  }
  const parts: string[] = []
  if (f.readState) {
    parts.push(`read_state=${f.readState}`)
  }
  if (f.bookedFrom) parts.push(`booked_from=${f.bookedFrom}`)
  if (f.bookedTo) parts.push(`booked_to=${f.bookedTo}`)
  if (f.q) {
    parts.push(`q=${f.q}`)
  }
  if (f.from) {
    parts.push(`from=${f.from}`)
  }
  if (f.to) {
    parts.push(`to=${f.to}`)
  }
  if (f.listings?.length) {
    parts.push(`listings=${[...f.listings].sort().join(',')}`)
  }
  if (f.groups?.length) {
    parts.push(`groups=${[...f.groups].sort().join(',')}`)
  }
  if (f.channels?.length) {
    parts.push(`channels=${[...f.channels].sort().join(',')}`)
  }
  if (f.statuses?.length) {
    parts.push(`statuses=${[...f.statuses].sort().join(',')}`)
  }
  if (f.stayDates?.length) {
    parts.push(`stay_dates=${[...f.stayDates].sort().join(',')}`)
  }
  if (f.endpoints?.length) {
    parts.push(`endpoints=${[...f.endpoints].sort().join(',')}`)
  }
  if (f.integrations?.length) {
    parts.push(`integrations=${[...f.integrations].sort().join(',')}`)
  }
  if (f.tags?.length) {
    parts.push(`tags=${[...f.tags].sort().join(',')}`)
  }
  if (f.reservationTags?.length) {
    parts.push(`reservation_tags=${[...f.reservationTags].sort().join(',')}`)
  }
  if (f.assignees?.length) {
    parts.push(`assignees=${[...f.assignees].sort().join(',')}`)
  }
  if (f.departments?.length) {
    parts.push(`departments=${[...f.departments].sort().join(',')}`)
  }
  if (f.contactIds?.length) {
    parts.push(`contacts=${[...f.contactIds].sort().join(',')}`)
  }
  if (f.inboxes?.length) {
    parts.push(`inboxes=${[...f.inboxes].sort().join(',')}`)
  }
  if (f.escalations) {
    parts.push('escalations=1')
  }
  appendResponseStatusSignature(parts, f.responseStatuses)
  if (f.aiReplied) {
    parts.push('ai_replied=1')
  }
  if (f.lastResponse) {
    parts.push(`last_response=${f.lastResponse}`)
  }
  if (f.assigned) {
    parts.push(`assigned=${f.assigned}`)
  }
  if (f.reservationCode) {
    parts.push(`reservation_code=${f.reservationCode}`)
  }
  if (f.scope) {
    parts.push(`scope=${f.scope}`)
  }
  return parts.join('&')
}

/** Timeline event as returned by `GET .../inbox-v2/conversations/[id]/timeline`. */
export interface TimelineEventV2 {
  actor_agent_color: string | null
  actor_agent_id: string | null
  actor_agent_name: string | null
  actor_contact_id: string | null
  actor_type: string | null
  actor_user_avatar_url: string | null
  actor_user_id: string | null
  actor_user_name: string | null
  conversation_integration_id: string | null
  conversation_integration_ota?: string | null
  created_at?: string | null
  direction: MessageDirectionV2
  error_message?: string | null
  id: string
  media?: TimelineMediaV2[]
  occurred_at: string
  payload: unknown
  preview: string | null
  source_id: string | null
  source_schema: string | null
  source_table: string | null
  /** Outbound lifecycle: SENDING (in flight), FAILED (provider rejected),
   *  UNCONFIRMED (delivery could not be established), or null (confirmed). */
  status?: string | null
  type: string
}

export interface TimelineMediaV2 {
  ai_caption: string | null
  external_url: string | null
  filename: string | null
  id: string
  internal_url: string | null
  mime_type: string | null
  size_bytes: number | null
  thumbnail_url: string | null
}

export interface TimelineResponse {
  data: TimelineEventV2[]
  next_cursor: string | null
  previous_cursor?: string | null
}

function appendResponseStatusSignature(parts: string[], statuses: string[] | undefined): void {
  if (statuses?.length) {
    parts.push(`response_status=${[...statuses].sort().join(',')}`)
  }
}
